import React, { useState, useRef, useMemo, useCallback, Fragment } from 'react';
import { isEmpty } from 'lodash';
import {
  TextInput,
  Divider,
} from 'oolib';
import { convertTagsValueToMultiValue } from '../../../../../../DynamicTagsInputs/utils';
import {
  StyledSearchWrapper,
  StyledActionButtonsContainer,
  StyledMenuContainer,
  StyledOptionsContainer
} from './styled';
import { ButtonGhost, ButtonPrimary, ButtonTertiary } from 'oolib/dist/v2/components/Buttons';
import { AddIndependentTagInput } from './comps/AddIndependentTagInput';
import { AddSubThemeTagInput } from './comps/AddSubThemeTagInput';

import { useTagData } from './utils/useTagData';
import { useTagFiltering } from './utils/useTagFiltering';
import { useTagSelection } from './utils/useTagSelection';
import { usePositioning } from './utils/usePositioning';
import { TagSection } from './comps/TagSection';
import { SubLightbox } from './comps/SubLightbox';
import { SelectedTagsPanel } from './comps/SelectedTagsPanel';
import {
  CONTAINER_WIDTH,
  SUB_LIGHTBOX_TOP_OFFSET
} from './constants';

export const SM_AnnoLightbox = ({
  onChange,
  value,
  tagTypesConfig = [],
  handleOnConfirm,
  handleOnCancel,
}) => {
  const containerRef = useRef(null);
  const themeSubLightboxRef = useRef(null); 
  const otherOptionsContainerRef = useRef(null);

  const [searchValue, setSearchValue] = useState('');
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [selectedSubTheme, setSelectedSubTheme] = useState(null);
  const [showSelectedPanel, setShowSelectedPanel] = useState(false);

  const { hierarchyConfig, optionsData, isLoading } = useTagData(tagTypesConfig);
  const { getFilteredOptions } = useTagFiltering(optionsData, selectedTheme, selectedSubTheme);
  const { handleTagSelection, getSelectionCount, getListProps } = useTagSelection(value, onChange);
  const { generatePositionStyle, generateLeftSideStyle } = usePositioning();

  const filteredOptions = useMemo(() =>
    getFilteredOptions(searchValue),
    [getFilteredOptions, searchValue]
  );

  // Tabs attached to the sub-themes config entry split that section into
  // multiple (e.g. Thematics + Attributes), scoping options client-side by
  // meta.taxonomyRole on each option.
  const subThemesTabs = useMemo(
    () => tagTypesConfig.find((c) => c.tagType === 'subThemes')?.tabs || null,
    [tagTypesConfig]
  );

  const matchesTab = useCallback((option, tab) => {
    if (tab.taxonomyRole === 'attribute') return option?.meta?.taxonomyRole === 'attribute';
    // "thematic" — anything not explicitly marked attribute (covers undefined/null/"thematic")
    return option?.meta?.taxonomyRole !== 'attribute';
  }, []);

  const handleThemeClick = useCallback(({ theme }) => {
    setSelectedTheme(theme);
    setSelectedSubTheme(null);
  }, []);

  const handleSubThemeClick = useCallback(({ subTheme, isIndependent = false }) => {
    setSelectedSubTheme(subTheme);
    if (isIndependent) {
      setSelectedTheme(null);
    }
  }, []);

  const handleListItemClick = useCallback(({ item, itemType, isIndependent = false }) => {
    if (itemType === 'tags') {
      handleTagSelection({ tag: item, tagType: 'tags' });
    } else if (itemType === 'themes') {
      handleThemeClick({ theme: item });
    } else if (itemType === 'subThemes') {
      handleSubThemeClick({ subTheme: item, isIndependent });
    }
  }, [handleTagSelection, handleThemeClick, handleSubThemeClick]);

  const handleIndependentSubThemeClick = useCallback(({ item, itemType }) => {
    handleListItemClick({ item, itemType, isIndependent: true });
  }, [handleListItemClick]);

  const handleIndependentTagClick = useCallback(({ item, itemType }) => {
    // Close all open lightboxes when clicking on independent tags
    setSelectedTheme(null);
    setSelectedSubTheme(null);
    // Handle the tag selection
    handleTagSelection({ tag: item, tagType: 'tags' });
  }, [handleTagSelection]);

  return (
    <div ref={containerRef} onMouseDown={(ev) => ev.preventDefault()} 
      style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: '0.6rem', 
        width: `${CONTAINER_WIDTH}rem`, 
        position: "relative" 
      }}
    >
      <StyledOptionsContainer ref={otherOptionsContainerRef}>
        <StyledActionButtonsContainer>
          {getSelectionCount('tags') ? 
            <ButtonGhost onClick={() => setShowSelectedPanel(prev => !prev)} value={`${getSelectionCount('tags')} selected`}/> 
            : undefined
          }
          <div style={{ display: "flex", gap: '0.4rem', justifyContent: "flex-end", flexGrow: 1 }}>
          
            <ButtonTertiary onClick={(e) => handleOnCancel(e)}>Cancel</ButtonTertiary>
            <ButtonPrimary disabled={isEmpty(value)} onClick={handleOnConfirm}>
              Apply
            </ButtonPrimary>
          </div>
        </StyledActionButtonsContainer>
      </StyledOptionsContainer>

      <StyledMenuContainer>

        {/* Search bar */}
        <StyledSearchWrapper>
          <TextInput
            S
            icon="MagnifyingGlass"
            placeholder="Search"
            value={searchValue}
            className={"UI_BODY_SM_DF"}
            onChange={(id, val) => setSearchValue(val)}
          />
          <AddIndependentTagInput
            style={{ marginTop: '0.4rem' }}
            optionsDataTags={optionsData.tags}
            value={value}
            onChange={onChange}
            allTagsGlobal={optionsData.tags}
          />
        </StyledSearchWrapper>

        {/* Themes Section */}
        {hierarchyConfig.hasThemes && (
          <Fragment>
            <TagSection
              title="THEMES"
              options={filteredOptions.themes}
              loading={isLoading.themes}
              listProps={getListProps({
                options: filteredOptions.themes,
                itemType: 'themes',
                isMulti: false,
                iconAfter: 'CaretRight',
                icon: "FolderSimple",
                valueProp: selectedTheme,
                onItemClick: handleListItemClick
              })}
            />
            <Divider style={{ margin: '0.4rem 0' }}/>
          </Fragment>
        )}

        

        {/* Sub-Themes Section(s) — single "Independent Sub-Themes" by default,
            or one section per tab (e.g. Thematics + Attributes) when tabs are configured. */}
        {hierarchyConfig.hasSubThemes && (
          subThemesTabs ? (
            subThemesTabs.map((tab) => {
              const tabOptions = filteredOptions.subThemes.filter((o) => matchesTab(o, tab));
              return (
                <Fragment key={tab.key}>
                  <TagSection
                    title={tab.display.toUpperCase()}
                    options={tabOptions}
                    loading={isLoading.subThemes}
                    listProps={getListProps({
                      options: tabOptions,
                      itemType: 'subThemes',
                      isMulti: false,
                      iconAfter: 'CaretRight',
                      icon: "FolderSimple",
                      valueProp: selectedSubTheme,
                      onItemClick: handleIndependentSubThemeClick
                    })}
                  />
                  <Divider style={{ margin: '0.4rem 0' }}/>
                </Fragment>
              );
            })
          ) : (
            <Fragment>
              <TagSection
                title="INDEPENDENT SUB-THEMES"
                options={filteredOptions.subThemes}
                loading={isLoading.subThemes}
                listProps={getListProps({
                  options: filteredOptions.subThemes,
                  itemType: 'subThemes',
                  isMulti: false,
                  iconAfter: 'CaretRight',
                  icon: "FolderSimple",
                  valueProp: selectedSubTheme,
                  onItemClick: handleIndependentSubThemeClick
                })}
              />
              <Divider style={{ margin: '0.4rem 0' }}/>
            </Fragment>
          )
        )}

        

        {/* Tags Section */}
        {hierarchyConfig.hasTags && (
          <TagSection
            title="INDEPENDENT TAGS"
            options={filteredOptions.tags}
            loading={isLoading.tags}
            listProps={getListProps({
              options: filteredOptions.tags,
              itemType: 'tags',
              isMulti: true,
              icon: "Tag",
              onItemClick: handleIndependentTagClick
            })}
          />
        )}
      
      </StyledMenuContainer>

      {/* Sub-lightbox for selected theme */}
      {selectedTheme && hierarchyConfig.hasSubThemes && (
        <SubLightbox
          top={otherOptionsContainerRef.current?.getBoundingClientRect().height + SUB_LIGHTBOX_TOP_OFFSET}
          forwardRef={themeSubLightboxRef}
          visibility="visible"
          style={generatePositionStyle({ containerRef, anchorRef: containerRef, selectedItem: selectedTheme })}
          loading={isLoading.subThemes}
          listProps={getListProps({
            options: filteredOptions.subThemesForSelectedTheme,
            itemType: 'subThemes',
            isMulti: false,
            iconAfter: 'CaretRight',
            icon: "FolderSimple",
            valueProp: selectedSubTheme,
            onItemClick: handleListItemClick
          })}
        />
      )}

      {/* Sub-lightbox for selected sub-theme */}
      {selectedSubTheme && hierarchyConfig.hasTags && (
        <SubLightbox
          top={otherOptionsContainerRef.current?.getBoundingClientRect().height + SUB_LIGHTBOX_TOP_OFFSET}
          visibility="visible"
          style={generatePositionStyle({ 
            containerRef, 
            anchorRef: selectedTheme ? themeSubLightboxRef : containerRef, 
            selectedItem: selectedSubTheme 
          })}
          loading={isLoading.tags}
          listProps={getListProps({
            options: selectedSubTheme ? filteredOptions.tagsForSelectedSubTheme : filteredOptions.tagsForSelectedTheme,
            itemType: 'tags',
            isMulti: true,
            icon: "Tag",
            onItemClick: handleListItemClick
          })}
        >
          <AddSubThemeTagInput
            selectedSubTheme={selectedSubTheme}
            style={{ marginBottom: '0.4rem' }}
            filteredTagsForSelectedSubTheme={filteredOptions.tagsForSelectedSubTheme || []}
            value={value}
            onChange={onChange}
            allTagsGlobal={optionsData.tags}
          />
        </SubLightbox>
      )}

      {/* Left-side lightbox for viewing/removing selected tags */}
      {showSelectedPanel && (
        <SelectedTagsPanel
          style={generateLeftSideStyle({ containerRef })}
          selectionCount={getSelectionCount('tags')}
          listProps={getListProps({
            options: convertTagsValueToMultiValue(value.tags),
            itemType: 'tags',
            isMulti: true,
            icon: 'Tag',
            onItemClick: handleListItemClick
          })}
        />
      )}
    </div>
  );
};


